import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  GripVertical,
  Settings,
  Trash2,
  Maximize2,
  Filter,
  AlertTriangle,
  AlertCircle,
  FolderOpen,
  Layers,
} from 'lucide-react';
import {
  CustomAnalysisWidget,
  AnalysisDataPoint,
  AnalysisQueryResult,
  AnalysisQueryFilter,
  getPaletteColor,
} from '../types';
import { analysisService } from '../analysisService';

interface AnalysisWidgetCardProps {
  widget: CustomAnalysisWidget;
  filters: AnalysisQueryFilter;
  isEditMode: boolean;
  onEditWidget: (widget: CustomAnalysisWidget) => void;
  onDeleteWidget: (widgetId: string) => void;
  onResizeWidget?: (widgetId: string, newWidth: number) => void;
  onCrossFilter: (dimension: string, value: string) => void;
  onOpenDrilldown?: (widget: CustomAnalysisWidget, dimensionValue?: string) => void;
  activeCrossFilterValue?: string;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>, widgetId: string) => void;
  onDragOver?: (e: React.DragEvent<HTMLDivElement>, widgetId: string) => void;
  onDrop?: (e: React.DragEvent<HTMLDivElement>, widgetId: string) => void;
}

export const AnalysisWidgetCard: React.FC<AnalysisWidgetCardProps> = ({
  widget,
  filters,
  isEditMode,
  onEditWidget,
  onDeleteWidget,
  onResizeWidget,
  onCrossFilter,
  onOpenDrilldown,
  activeCrossFilterValue,
  onDragStart,
  onDragOver,
  onDrop,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [dataResult, setDataResult] = useState<AnalysisQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch widget data when filters or widget props change
  useEffect(() => {
    let isCancelled = false;
    setLoading(true);
    setError(null);

    const isPreview = widget.id === 'PREVIEW_TEMP' || widget.id.startsWith('PREVIEW');

    const queryPromise = isPreview
      ? analysisService.previewQuery(
          {
            title: widget.title,
            chartType: widget.chartType,
            dataSource: widget.dataSource,
            dimension: widget.dimension,
            metric: widget.metric,
            aggregation: widget.aggregation,
            widthCol: widget.widthCol,
            heightPx: widget.heightPx,
            colorPalette: widget.colorPalette,
            customColor: widget.customColor,
            fontSize: widget.fontSize,
            showLegend: widget.showLegend,
            showGrid: widget.showGrid,
            showLabels: widget.showLabels,
            targetThreshold: widget.targetThreshold,
            warningThreshold: widget.warningThreshold,
            criticalThreshold: widget.criticalThreshold,
            enableCrossFilter: widget.enableCrossFilter,
          },
          filters
        )
      : analysisService.queryWidgetData(widget.id, filters);

    queryPromise
      .then((res) => {
        if (!isCancelled) {
          setDataResult(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!isCancelled) {
          setError(err.message || 'Failed to load widget data');
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [
    widget.id,
    widget.updatedAt,
    widget.dataSource,
    widget.dimension,
    widget.metric,
    widget.aggregation,
    widget.chartType,
    widget.targetThreshold,
    widget.warningThreshold,
    widget.criticalThreshold,
    filters,
  ]);

  // Handle ALL possible data formats:
  // 1. Raw array: [ { label: '...', value: 12 }, ... ]
  // 2. Object with .data: { data: [ { label: '...', value: 12 } ] }
  // 3. Object with .series: { series: [ { label: '...', value: 12 } ] }
  const rawData: AnalysisDataPoint[] = React.useMemo(() => {
    if (!dataResult) return [];
    if (Array.isArray(dataResult)) return dataResult;
    if (Array.isArray(dataResult.data)) return dataResult.data;
    if (Array.isArray(dataResult.series)) return dataResult.series;
    return [];
  }, [dataResult]);

  const totalCount: number = React.useMemo(() => {
    if (!dataResult) return 0;
    if (Array.isArray(dataResult)) {
      return dataResult.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
    }
    if (typeof dataResult.total === 'number') return dataResult.total;
    if (typeof dataResult.totalCount === 'number') return dataResult.totalCount;
    return rawData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
  }, [dataResult, rawData]);

  const summary = Array.isArray(dataResult) ? undefined : dataResult?.summary;

  // Threshold status indicator
  const thresholdStatus =
    summary?.thresholdStatus ||
    (!Array.isArray(dataResult) && dataResult?.thresholdStatus
      ? (dataResult.thresholdStatus.toUpperCase() as 'NORMAL' | 'WARNING' | 'CRITICAL')
      : 'NORMAL');

  // Clicking an interactive chart element triggers crossFilter and opens drilldown
  const handleItemClick = (label: string) => {
    if (widget.enableCrossFilter) {
      onCrossFilter(widget.dimension, label);
    }
    if (onOpenDrilldown) {
      onOpenDrilldown(widget, label);
    }
  };

  const nextWidth = (current: number): number => {
    if (current === 4) return 6;
    if (current === 6) return 8;
    if (current === 8) return 12;
    if (current === 12) return 4;
    return 6;
  };

  const chartHeight = Math.max(widget.heightPx - 70, 240);

  return (
    <div
      className={`analysis-widget-card analysis-col-${widget.widthCol}`}
      style={{
        gridColumn: `span ${widget.widthCol} / span ${widget.widthCol}`,
        height: `${widget.heightPx}px`,
        border: activeCrossFilterValue ? '2px solid #2563eb' : '1px solid #e2e8f0',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        boxShadow: activeCrossFilterValue
          ? '0 6px 18px rgba(37, 99, 235, 0.2)'
          : '0 1px 3px rgba(0, 0, 0, 0.04)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.2s ease',
      }}
      draggable={isEditMode}
      onDragStart={(e) => isEditMode && onDragStart && onDragStart(e, widget.id)}
      onDragOver={(e) => isEditMode && onDragOver && onDragOver(e, widget.id)}
      onDrop={(e) => isEditMode && onDrop && onDrop(e, widget.id)}
    >
      {/* Widget Header */}
      <div
        className="analysis-widget-header"
        style={{
          minHeight: '50px',
          padding: '10px 16px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#ffffff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
          {isEditMode && (
            <div
              style={{ cursor: 'grab', color: '#94a3b8', display: 'flex', alignItems: 'center' }}
              title="Drag to reorder"
            >
              <GripVertical size={16} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <h3
              style={{
                fontSize: widget.fontSize === 'lg' ? '15px' : '13px',
                fontWeight: 700,
                color: '#0f172a',
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={widget.title}
            >
              {widget.title}
            </h3>
            <span
              style={{
                fontSize: '11px',
                color: '#64748b',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '1px',
              }}
            >
              <span style={{ textTransform: 'capitalize' }}>{widget.dataSource}</span>
              <span>•</span>
              <span style={{ textTransform: 'capitalize' }}>{widget.dimension}</span>
              <span>•</span>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  padding: '1px 5px',
                  borderRadius: '4px',
                  backgroundColor: '#eff6ff',
                  color: '#2563eb',
                  border: '1px solid #bfdbfe',
                }}
                title={`Hesaplama: ${widget.aggregation || 'AVG'}`}
              >
                {widget.aggregation || 'AVG'}
              </span>
              {widget.enableCrossFilter && (
                <span
                  style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center' }}
                  title="Interactive cross-filtering active"
                >
                  <Filter size={10} style={{ marginLeft: '2px' }} />
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Header Right: Status Badges, Drilldown & Edit Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {/* Threshold alert chip */}
          {thresholdStatus === 'CRITICAL' && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 7px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 700,
                backgroundColor: '#fee2e2',
                color: '#b91c1c',
                border: '1px solid #fca5a5',
              }}
            >
              <AlertCircle size={11} /> Critical
            </span>
          )}
          {thresholdStatus === 'WARNING' && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                padding: '2px 7px',
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 700,
                backgroundColor: '#fef3c7',
                color: '#b45309',
                border: '1px solid #fcd34d',
              }}
            >
              <AlertTriangle size={11} /> Warning
            </span>
          )}

          {/* Active Filter Pill indicator */}
          {activeCrossFilterValue && (
            <span
              style={{
                padding: '2px 8px',
                borderRadius: '9999px',
                fontSize: '11px',
                backgroundColor: '#eff6ff',
                color: '#1d4ed8',
                border: '1px solid #bfdbfe',
                fontWeight: 600,
              }}
            >
              Filter: {activeCrossFilterValue}
            </span>
          )}

          {/* Drill-down / "Drill Down" Button */}
          {onOpenDrilldown && (
            <button
              type="button"
              onClick={() => onOpenDrilldown(widget)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 9px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#f8fafc',
                color: '#2563eb',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title="Inspect underlying safety reports and raw records for this widget"
            >
              <FolderOpen size={12} /> Drill Down
            </button>
          )}

          {/* Edit Mode Quick Actions */}
          {isEditMode && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: '#f8fafc',
                padding: '3px 5px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginLeft: '4px',
              }}
            >
              {onResizeWidget && (
                <button
                  type="button"
                  onClick={() => onResizeWidget(widget.id, nextWidth(widget.widthCol))}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#64748b',
                    padding: '3px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                  title={`Resize Width: ${widget.widthCol}/12`}
                >
                  <Maximize2 size={13} />
                </button>
              )}
              <button
                type="button"
                onClick={() => onEditWidget(widget)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  padding: '3px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                title="Edit Widget Settings"
              >
                <Settings size={13} />
              </button>
              <button
                type="button"
                onClick={() => onDeleteWidget(widget.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#dc2626',
                  padding: '3px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                title="Delete Widget"
              >
                <Trash2 size={13} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Widget Body */}
      <div
        className="analysis-widget-body"
        style={{
          height: `${chartHeight}px`,
          padding: '10px 14px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              gap: '8px',
            }}
          >
            <div
              style={{
                width: '24px',
                height: '24px',
                border: '3px solid #2563eb',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <span style={{ fontSize: '11px', color: '#64748b' }}>Calculating analytical aggregations...</span>
          </div>
        ) : error ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              fontSize: '12px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <AlertCircle size={22} style={{ marginBottom: '6px', color: '#f87171' }} />
            <span>{error}</span>
          </div>
        ) : rawData.length === 0 ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94a3b8',
              fontSize: '12px',
              padding: '16px',
              textAlign: 'center',
            }}
          >
            <Layers size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <span>No data records found matching active filter criteria</span>
          </div>
        ) : (
          <div
            className="analysis-chart-wrapper"
            style={{
              width: '100%',
              height: `${chartHeight - 10}px`,
              minHeight: '200px',
              position: 'relative',
            }}
          >
            {renderChartContent(widget, rawData, totalCount, chartHeight, handleItemClick)}
          </div>
        )}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Visual Chart Dispatcher with guaranteed pixel-size rendering & complete axes
// ----------------------------------------------------------------------------
function renderChartContent(
  widget: CustomAnalysisWidget,
  data: AnalysisDataPoint[],
  totalCount: number,
  chartHeight: number,
  onItemClick: (label: string) => void
) {
  const primaryColor = widget.customColor || getPaletteColor(widget.colorPalette, 0);

  // 1. KPI Metric Counter View
  if (widget.chartType === 'kpi') {
    const isAlert =
      widget.criticalThreshold && totalCount >= widget.criticalThreshold
        ? 'critical'
        : widget.warningThreshold && totalCount >= widget.warningThreshold
        ? 'warning'
        : 'normal';

    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '16px',
          cursor: 'pointer',
        }}
        onClick={() => onItemClick('ALL')}
      >
        <div
          style={{
            fontSize: '46px',
            fontWeight: 800,
            letterSpacing: '-1px',
            color:
              isAlert === 'critical' ? '#dc2626' : isAlert === 'warning' ? '#d97706' : '#0f172a',
          }}
        >
          {totalCount.toLocaleString()}
        </div>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginTop: '4px' }}>
          {widget.metric.toUpperCase()} • {widget.dimension}
        </div>

        {/* Target & Threshold Indicator Bars */}
        {(widget.targetThreshold || widget.warningThreshold || widget.criticalThreshold) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '16px',
              fontSize: '11px',
              fontWeight: 600,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {widget.targetThreshold !== undefined && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#ecfdf5',
                  color: '#047857',
                  border: '1px solid #a7f3d0',
                }}
              >
                Target: {widget.targetThreshold}
              </span>
            )}
            {widget.warningThreshold !== undefined && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#fffbeb',
                  color: '#b45309',
                  border: '1px solid #fde68a',
                }}
              >
                Warning: ≥{widget.warningThreshold}
              </span>
            )}
            {widget.criticalThreshold !== undefined && (
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '6px',
                  backgroundColor: '#fef2f2',
                  color: '#b91c1c',
                  border: '1px solid #fecaca',
                }}
              >
                Critical: ≥{widget.criticalThreshold}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // 2. Data Table View
  if (widget.chartType === 'table') {
    const maxVal = Math.max(...data.map((d) => d.value), 1);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid #e2e8f0',
                color: '#64748b',
                fontSize: '11px',
                textTransform: 'uppercase',
                backgroundColor: '#f8fafc',
              }}
            >
              <th style={{ padding: '8px 10px', fontWeight: 600 }}>{widget.dimension}</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Value</th>
              <th style={{ padding: '8px 10px', width: '35%', fontWeight: 600 }}>Distribution</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => {
              const pct = Math.round((row.value / maxVal) * 100);
              const color = getPaletteColor(widget.colorPalette, idx, widget.customColor);
              return (
                <tr
                  key={idx}
                  onClick={() => onItemClick(row.label)}
                  style={{
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }} title={row.label}>
                    {row.label}
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                    {row.value}
                  </td>
                  <td style={{ padding: '8px 10px' }}>
                    <div
                      style={{
                        width: '100%',
                        backgroundColor: '#f1f5f9',
                        borderRadius: '9999px',
                        height: '6px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          borderRadius: '9999px',
                          width: `${pct}%`,
                          backgroundColor: color,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // 3. Line Trend Chart
  if (widget.chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
        <LineChart data={data} margin={{ top: 12, right: 20, left: 0, bottom: 25 }}>
          {widget.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />}
          <XAxis
            dataKey="label"
            stroke="#64748b"
            fontSize={11}
            tickLine={false}
            height={30}
            tick={{ fontSize: 11, fill: '#475569' }}
          />
          <YAxis stroke="#64748b" fontSize={11} tickLine={false} width={38} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              fontSize: '12px',
            }}
          />
          {widget.showLegend && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} />}
          {widget.targetThreshold !== undefined && (
            <ReferenceLine
              y={widget.targetThreshold}
              stroke="#10b981"
              strokeDasharray="4 4"
              label={{ value: `Target: ${widget.targetThreshold}`, fill: '#059669', fontSize: 10 }}
            />
          )}
          {widget.warningThreshold !== undefined && (
            <ReferenceLine
              y={widget.warningThreshold}
              stroke="#f59e0b"
              strokeDasharray="4 4"
              label={{ value: `Alert-1: ${widget.warningThreshold}`, fill: '#d97706', fontSize: 10 }}
            />
          )}
          {widget.criticalThreshold !== undefined && (
            <ReferenceLine
              y={widget.criticalThreshold}
              stroke="#ef4444"
              strokeDasharray="4 4"
              label={{ value: `Alert-2: ${widget.criticalThreshold}`, fill: '#dc2626', fontSize: 10 }}
            />
          )}
          <Line
            type="monotone"
            dataKey="value"
            name={widget.title}
            stroke={primaryColor}
            strokeWidth={3}
            dot={{ r: 4, fill: primaryColor, cursor: 'pointer' }}
            activeDot={{ r: 7, onClick: (_e: any, payload: any) => onItemClick(payload?.payload?.label) }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // 4. Area Chart
  if (widget.chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
        <AreaChart data={data} margin={{ top: 12, right: 20, left: 0, bottom: 25 }}>
          <defs>
            <linearGradient id={`colorGrad_${widget.id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.4} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          {widget.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />}
          <XAxis
            dataKey="label"
            stroke="#64748b"
            fontSize={11}
            tickLine={false}
            height={30}
            tick={{ fontSize: 11, fill: '#475569' }}
          />
          <YAxis stroke="#64748b" fontSize={11} tickLine={false} width={38} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              fontSize: '12px',
            }}
          />
          {widget.showLegend && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} />}
          <Area
            type="monotone"
            dataKey="value"
            name={widget.title}
            stroke={primaryColor}
            fillOpacity={1}
            fill={`url(#colorGrad_${widget.id})`}
            onClick={(entry: any) => entry && onItemClick(entry.label)}
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // 5. Pie / Donut Chart
  if (widget.chartType === 'pie' || widget.chartType === 'donut') {
    const isDonut = widget.chartType === 'donut';
    return (
      <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
        <PieChart margin={{ top: 5, bottom: 10, left: 5, right: 5 }}>
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              fontSize: '12px',
            }}
          />
          {widget.showLegend && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />}
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            innerRadius={isDonut ? '45%' : '0%'}
            outerRadius="75%"
            paddingAngle={isDonut ? 3 : 1}
            dataKey="value"
            nameKey="label"
            label={widget.showLabels ? ({ name, percent }: any) => `${name} (${(percent * 100).toFixed(0)}%)` : false}
            onClick={(entry: any) => entry && onItemClick(entry.label)}
            cursor="pointer"
          >
            {data.map((_entry, index) => (
              <Cell key={`cell-${index}`} fill={getPaletteColor(widget.colorPalette, index, widget.customColor)} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // 6. Radar Chart
  if (widget.chartType === 'radar') {
    return (
      <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
        <RadarChart data={data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
          <PolarGrid stroke="#e2e8f0" />
          <PolarAngleAxis dataKey="label" stroke="#475569" fontSize={11} />
          <PolarRadiusAxis stroke="#cbd5e1" fontSize={10} />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              fontSize: '12px',
            }}
          />
          <Radar
            name={widget.title}
            dataKey="value"
            stroke={primaryColor}
            fill={primaryColor}
            fillOpacity={0.4}
            onClick={(entry: any) => entry && onItemClick(entry.label)}
          />
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  // 7. Horizontal Bar Chart
  if (widget.chartType === 'horizontal_bar') {
    return (
      <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
        <BarChart layout="vertical" data={data} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
          {widget.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />}
          <XAxis type="number" stroke="#64748b" fontSize={11} tickLine={false} allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="label"
            stroke="#475569"
            fontSize={11}
            tickLine={false}
            width={120}
            tick={{ fontSize: 11, fill: '#334155' }}
          />
          <Tooltip
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              fontSize: '12px',
            }}
          />
          {widget.showLegend && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }} />}
          {widget.targetThreshold !== undefined && (
            <ReferenceLine
              x={widget.targetThreshold}
              stroke="#10b981"
              strokeDasharray="4 4"
              label={{ value: `AVG: ${widget.targetThreshold}`, fill: '#059669', fontSize: 10 }}
            />
          )}
          {widget.warningThreshold !== undefined && (
            <ReferenceLine
              x={widget.warningThreshold}
              stroke="#f59e0b"
              strokeDasharray="4 4"
              label={{ value: `Alert: ${widget.warningThreshold}`, fill: '#d97706', fontSize: 10 }}
            />
          )}
          {widget.criticalThreshold !== undefined && (
            <ReferenceLine
              x={widget.criticalThreshold}
              stroke="#ef4444"
              strokeDasharray="4 4"
              label={{ value: `Critical: ${widget.criticalThreshold}`, fill: '#dc2626', fontSize: 10 }}
            />
          )}
          <Bar
            dataKey="value"
            name={widget.title}
            radius={[0, 4, 4, 0]}
            onClick={(entry: any) => entry && onItemClick(entry.label)}
            cursor="pointer"
          >
            {data.map((_entry, index) => (
              <Cell key={`hbar-${index}`} fill={getPaletteColor(widget.colorPalette, index, widget.customColor)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Default: Vertical Bar Chart
  return (
    <ResponsiveContainer width="100%" height={chartHeight - 15} minWidth={100} minHeight={200}>
      <BarChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 40 }}>
        {widget.showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />}
        <XAxis
          dataKey="label"
          stroke="#64748b"
          fontSize={11}
          tickLine={false}
          interval={0}
          angle={-22}
          textAnchor="end"
          height={45}
          tick={{ fontSize: 11, fill: '#475569' }}
        />
        <YAxis stroke="#64748b" fontSize={11} tickLine={false} width={38} allowDecimals={false} />
        <Tooltip
          contentStyle={{
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            fontSize: '12px',
          }}
        />
        {widget.showLegend && <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '6px' }} />}
        {widget.targetThreshold !== undefined && (
          <ReferenceLine
            y={widget.targetThreshold}
            stroke="#10b981"
            strokeDasharray="4 4"
            label={{ value: `Target: ${widget.targetThreshold}`, fill: '#059669', fontSize: 10 }}
          />
        )}
        {widget.warningThreshold !== undefined && (
          <ReferenceLine
            y={widget.warningThreshold}
            stroke="#f59e0b"
            strokeDasharray="4 4"
            label={{ value: `Warning: ${widget.warningThreshold}`, fill: '#d97706', fontSize: 10 }}
          />
        )}
        {widget.criticalThreshold !== undefined && (
          <ReferenceLine
            y={widget.criticalThreshold}
            stroke="#ef4444"
            strokeDasharray="4 4"
            label={{ value: `Critical: ${widget.criticalThreshold}`, fill: '#dc2626', fontSize: 10 }}
          />
        )}
        <Bar
          dataKey="value"
          name={widget.title}
          radius={[4, 4, 0, 0]}
          onClick={(entry: any) => entry && onItemClick(entry.label)}
          cursor="pointer"
        >
          {data.map((_entry, index) => (
            <Cell key={`vbar-${index}`} fill={getPaletteColor(widget.colorPalette, index, widget.customColor)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
